import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import ExchangeFields from "../../../UI/ExchangeFields";
import { getCurrencyDisplayLabel } from "../../../../utils/currencyDisplay";
import { useMemo, useState } from "react";
import { BiChevronDown, BiReset } from "react-icons/bi";
import { ExchangeData } from "../../../../types/budget/budget";
import { toast } from "react-toastify";
import { isDateValid } from "../../../../utils/utils";
import { addHours, format } from "date-fns";
import { EventScopeEnum } from "../../../../types/project/enums";

type Props = {
  savedExchangeData?: ExchangeData;
  compareExchangeData?: ExchangeData;
  onSubmitExchange: (exchangeData: ExchangeData) => void;
  eventExchangeData?: ExchangeData;
  hasOverride?: boolean;
  onResetOverride?: () => void;
  showOverrideIndicator?: boolean;
  eventScope?: string;
};

const Exchange: React.FC<Props> = ({
  savedExchangeData,
  compareExchangeData,
  onSubmitExchange,
  eventExchangeData,
  hasOverride = false,
  onResetOverride,
  showOverrideIndicator = false,
  eventScope,
}: Props) => {
  const [exchangeState, setExchangeState] = useState({
    date: "",
    currency: "",
    value: 0,
  });

  function handleChange(value: any, field: string) {
    if (field === "monetary-change") {
      return setExchangeState({
        ...exchangeState,
        currency: value.currency,
        value: value.value,
      });
    }
    setExchangeState({ ...exchangeState, [field]: value });
  }
  function updateCalcValues(type: string, value: number, calcType: string) {
    handleChange(
      {
        currency: calcType,
        value,
      },
      "monetary-change"
    );
  }

  useMemo(() => {
    if (!!savedExchangeData) setExchangeState({ ...savedExchangeData });
  }, [savedExchangeData]);

  function submitExchange() {
    // Verifica se foi editado manualmente (diferente do padrão do evento)
    const isManualEdit = eventExchangeData && (
      exchangeState.date !== eventExchangeData.date ||
      exchangeState.currency !== eventExchangeData.currency ||
      exchangeState.value !== eventExchangeData.value
    );
    
    onSubmitExchange({
      ...exchangeState,
      isOverride: isManualEdit || hasOverride,
    } as any);
    toast.success("Câmbio salvo com sucesso!");
  }
  
  function handleResetToDefault() {
    if (eventExchangeData && onResetOverride) {
      setExchangeState({
        date: eventExchangeData.date,
        currency: eventExchangeData.currency,
        value: eventExchangeData.value,
      });
      onResetOverride();
      toast.success("Câmbio restaurado para o padrão do evento!");
    }
  }

  if (eventScope === EventScopeEnum.NATIONAL) {
    return null;
  }

  return (
    <Accordion
      sx={{ my: 1.5, borderRadius: "4px" }}
      TransitionProps={{ unmountOnExit: true }}
      defaultExpanded
    >
      <AccordionSummary
        aria-controls="panel1a-content"
        expandIcon={<BiChevronDown />}
        id="panel1a-header"
        sx={{
          minHeight: 40,
          maxHeight: 40,
          borderRadius: "4px",
          "&.Mui-expanded": {
            minHeight: 40,
            maxHeight: 40,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <span style={{ fontWeight: "bold" }}>Câmbio</span>
          {showOverrideIndicator && hasOverride && (
            <Chip
              label="Personalizado"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </AccordionSummary>
      <Box>{/* total summary */}</Box>

      <AccordionDetails>
        <ExchangeFields
          date={exchangeState.date}
          currency={exchangeState.currency}
          value={exchangeState.value}
          onDateChange={(date) => handleChange(date, "date")}
          onExchangeChange={(currency, value) => updateCalcValues("", value, currency)}
          disabled={false}
          dateHelperText={
            !!compareExchangeData && isDateValid(compareExchangeData.date)
              ? format(
                  addHours(new Date(compareExchangeData.date), 5),
                  "dd/MM/yyyy"
                )
              : undefined
          }
          valueHelperText={
            !!compareExchangeData
              ? `${getCurrencyDisplayLabel(compareExchangeData.currency)} ${compareExchangeData.value}`
              : undefined
          }
        />
        <Box display={"flex"} gap={1} justifyContent="flex-end">
          {showOverrideIndicator && hasOverride && onResetOverride && (
            <Tooltip title="Restaurar câmbio padrão do evento">
              <Button
                onClick={handleResetToDefault}
                startIcon={<BiReset />}
                variant="outlined"
                color="secondary"
              >
                Restaurar Padrão
              </Button>
            </Tooltip>
          )}
          <Button
            onClick={submitExchange}
            variant="outlined"
          >
            Salvar
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default Exchange;
